/* @jsxImportSource @emotion/react */
import type { HTMLAttributes } from 'react'
import React from 'react'
import { css } from '@emotion/react'

import {
  websiteSectionHorizontalPadding as defaultContainerHorizontalPadding,
  websiteSectionContainerMaxWidth as defaultContainerMaxWidth,
  websiteSectionMobileHorizontalPadding as defaultContainerMobileHorizontalPadding,
  websiteSectionVerticalPadding as defaultContainerVerticalPadding,
  websiteSectionSmallBreakpoint as SMALL_BREAKPOINT,
} from './constants'

type WebsiteSectionProps = {
  children?: React.ReactNode
  containerVerticalPadding?: number
  containerTopPadding?: number
  containerBottomPadding?: number
  containerLeftPadding?: number
  containerRightPadding?: number
  maxWidth?: number | 'none'
  background?: string
  isFirstSection?: boolean
  firstSectionExtraPadding?: number
  innerStyle?: React.CSSProperties
  /** Landmark element to render as, e.g. `footer`. Defaults to `section`. */
  as?: 'section' | 'footer' | 'header' | 'aside' | 'div'
  ref?: React.RefObject<HTMLDivElement>
} & HTMLAttributes<HTMLDivElement>

export function WebsiteSection({
  children,
  containerVerticalPadding,
  containerTopPadding,
  containerBottomPadding,
  containerLeftPadding,
  containerRightPadding,
  background,
  innerStyle,
  isFirstSection = false,
  firstSectionExtraPadding = 0,
  maxWidth,
  as: Tag = 'section',
  ...props
}: WebsiteSectionProps) {
  let topPadding = defaultContainerVerticalPadding
  if (typeof containerVerticalPadding === 'number') {
    topPadding = containerVerticalPadding
  } else if (typeof containerTopPadding === 'number') {
    topPadding = containerTopPadding
  }

  let bottomPadding = defaultContainerVerticalPadding
  if (typeof containerVerticalPadding === 'number') {
    bottomPadding = containerVerticalPadding
  } else if (typeof containerBottomPadding === 'number') {
    bottomPadding = containerBottomPadding
  }
  const extraPaddingForNavbar = isFirstSection ? firstSectionExtraPadding : 0
  return (
    <Tag
      css={css`
        position: relative; // with backgrounds; we need all sections to be in the zindex flow
        z-index: 1;
        padding: ${topPadding + extraPaddingForNavbar}px 0 ${bottomPadding}px;
        background-color: ${background ? `var(--${background})` : 'transparent'};
        @media (max-width: ${SMALL_BREAKPOINT}px) {
          padding: ${topPadding * 0.7 + extraPaddingForNavbar}px 0
            ${bottomPadding * 0.7}px;
        }
      `}
      {...props}
    >
      <div
        css={css`
          max-width: ${
            maxWidth === 'none'
              ? 'none'
              : `${maxWidth ?? defaultContainerMaxWidth}px`
          };
          padding: 0
            ${containerRightPadding ?? defaultContainerHorizontalPadding}px 0
            ${containerLeftPadding ?? defaultContainerHorizontalPadding}px;
          margin: 0 auto;
          @media (max-width: ${SMALL_BREAKPOINT}px) {
            padding: 0 ${defaultContainerMobileHorizontalPadding}px;
          }
        `}
        style={innerStyle}
      >
        {children}
      </div>
    </Tag>
  )
}
